/**
 * CriteriaBuilder Component
 *
 * Builds condition criteria with AND/OR logic.
 * Reusable for entry criteria and decision groups.
 * Uses Standard Component Library throughout.
 *
 * @component
 */

import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Info
} from 'lucide-react';
import {
  StandardButton,
  StandardInput,
  StandardSelect,
  StandardCard
} from '../../standard';
import type {
  ProcessCondition,
  ProcessConditionInput,
  ConditionOperator
} from '../../../types/processBuilder';

// ============================================
// INTERFACES
// ============================================

interface CriteriaBuilderProps {
  conditions: ProcessConditionInput[];
  onChange: (conditions: ProcessConditionInput[]) => void;
  conditionLogic: 'AND' | 'OR' | 'CUSTOM';
  onLogicChange: (logic: 'AND' | 'OR' | 'CUSTOM') => void;
  customLogic?: string;
  onCustomLogicChange?: (logic: string) => void;
  objectType: string;
  title?: string;
  description?: string;
  showLogicOptions?: boolean;
  disabled?: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const LOGIC_OPTIONS = [
  { value: 'AND', label: 'All conditions are met (AND)' },
  { value: 'OR', label: 'Any condition is met (OR)' },
  { value: 'CUSTOM', label: 'Custom logic' }
];

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string; requiresValue: boolean }[] = [
  { value: 'equals', label: 'Equals', requiresValue: true },
  { value: 'not_equals', label: 'Not Equals', requiresValue: true },
  { value: 'greater_than', label: 'Greater Than', requiresValue: true },
  { value: 'less_than', label: 'Less Than', requiresValue: true },
  { value: 'greater_or_equal', label: 'Greater or Equal', requiresValue: true },
  { value: 'less_or_equal', label: 'Less or Equal', requiresValue: true },
  { value: 'contains', label: 'Contains', requiresValue: true },
  { value: 'not_contains', label: 'Does Not Contain', requiresValue: true },
  { value: 'starts_with', label: 'Starts With', requiresValue: true },
  { value: 'ends_with', label: 'Ends With', requiresValue: true },
  { value: 'is_null', label: 'Is Empty', requiresValue: false },
  { value: 'is_not_null', label: 'Is Not Empty', requiresValue: false },
  { value: 'is_changed', label: 'Is Changed', requiresValue: false },
  { value: 'is_changed_to', label: 'Is Changed To', requiresValue: true },
  { value: 'is_changed_from', label: 'Is Changed From', requiresValue: true },
  { value: 'in_list', label: 'In List', requiresValue: true },
  { value: 'not_in_list', label: 'Not In List', requiresValue: true }
];

const FIELD_OPTIONS_BY_OBJECT: Record<string, { value: string; label: string; type: string }[]> = {
  Lead: [
    { value: 'Status', label: 'Status', type: 'picklist' },
    { value: 'LeadScore', label: 'Lead Score', type: 'number' },
    { value: 'Source', label: 'Lead Source', type: 'picklist' },
    { value: 'Rating', label: 'Rating', type: 'picklist' },
    { value: 'CompanySize', label: 'Company Size', type: 'number' },
    { value: 'Email', label: 'Email', type: 'string' },
    { value: 'Phone', label: 'Phone', type: 'string' },
    { value: 'Industry', label: 'Industry', type: 'picklist' }
  ],
  Opportunity: [
    { value: 'Stage', label: 'Stage', type: 'picklist' },
    { value: 'Amount', label: 'Amount', type: 'number' },
    { value: 'Probability', label: 'Probability', type: 'number' },
    { value: 'CloseDate', label: 'Close Date', type: 'date' },
    { value: 'Type', label: 'Type', type: 'picklist' },
    { value: 'LeadSource', label: 'Lead Source', type: 'picklist' }
  ],
  Case: [
    { value: 'Status', label: 'Status', type: 'picklist' },
    { value: 'Priority', label: 'Priority', type: 'picklist' },
    { value: 'Type', label: 'Type', type: 'picklist' },
    { value: 'Origin', label: 'Origin', type: 'picklist' },
    { value: 'IsEscalated', label: 'Is Escalated', type: 'boolean' },
    { value: 'Subject', label: 'Subject', type: 'string' }
  ],
  Contract: [
    { value: 'Status', label: 'Status', type: 'picklist' },
    { value: 'ContractValue', label: 'Contract Value', type: 'number' },
    { value: 'StartDate', label: 'Start Date', type: 'date' },
    { value: 'EndDate', label: 'End Date', type: 'date' },
    { value: 'AutoRenewal', label: 'Auto Renewal', type: 'boolean' }
  ],
  Contact: [
    { value: 'EmailOptIn', label: 'Email Opt In', type: 'boolean' },
    { value: 'Email', label: 'Email', type: 'string' },
    { value: 'Phone', label: 'Phone', type: 'string' },
    { value: 'AccountId', label: 'Account ID', type: 'lookup' }
  ],
  Account: [
    { value: 'Type', label: 'Type', type: 'picklist' },
    { value: 'Industry', label: 'Industry', type: 'picklist' },
    { value: 'AnnualRevenue', label: 'Annual Revenue', type: 'number' },
    { value: 'NumberOfEmployees', label: 'Number of Employees', type: 'number' },
    { value: 'IsVIP', label: 'Is VIP', type: 'boolean' }
  ],
  Task: [
    { value: 'Status', label: 'Status', type: 'picklist' },
    { value: 'Priority', label: 'Priority', type: 'picklist' },
    { value: 'ActivityDate', label: 'Due Date', type: 'date' }
  ],
  default: [
    { value: 'Status', label: 'Status', type: 'picklist' },
    { value: 'Priority', label: 'Priority', type: 'picklist' },
    { value: 'CreatedDate', label: 'Created Date', type: 'date' },
    { value: 'LastModifiedDate', label: 'Last Modified Date', type: 'date' }
  ]
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const generateId = (): string => {
  return `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getFieldOptions = (objectType: string) => {
  return FIELD_OPTIONS_BY_OBJECT[objectType] || FIELD_OPTIONS_BY_OBJECT.default;
};

const operatorRequiresValue = (operator: ConditionOperator): boolean => {
  const opDef = OPERATOR_OPTIONS.find(op => op.value === operator);
  return opDef?.requiresValue ?? true;
};

// ============================================
// CONDITION ROW COMPONENT
// ============================================

interface ConditionRowProps {
  condition: ProcessConditionInput;
  index: number;
  objectType: string;
  onUpdate: (index: number, field: keyof ProcessConditionInput, value: any) => void;
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  isFirst: boolean;
  isLast: boolean;
  disabled?: boolean;
  showRowNumber?: boolean;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  index,
  objectType,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  disabled,
  showRowNumber = true
}) => {
  const fieldOptions = getFieldOptions(objectType);
  const showValue = operatorRequiresValue(condition.operator);

  return (
    <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg group">
      {/* Row Number */}
      {showRowNumber && (
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium flex items-center justify-center">
          {index + 1}
        </div>
      )}

      {/* Drag Handle (visual only for now) */}
      <div className="flex-shrink-0 text-gray-400 dark:text-gray-600 cursor-grab">
        <GripVertical size={16} />
      </div>

      {/* Field Select */}
      <div className="flex-1 min-w-[150px]">
        <StandardSelect
          value={condition.field}
          onChange={(e) => onUpdate(index, 'field', e.target.value)}
          options={[
            { value: '', label: 'Select field...' },
            ...fieldOptions.map(f => ({ value: f.value, label: f.label }))
          ]}
          disabled={disabled}
        />
      </div>

      {/* Operator Select */}
      <div className="flex-1 min-w-[150px]">
        <StandardSelect
          value={condition.operator}
          onChange={(e) => onUpdate(index, 'operator', e.target.value as ConditionOperator)}
          options={OPERATOR_OPTIONS.map(op => ({ value: op.value, label: op.label }))}
          disabled={disabled}
        />
      </div>

      {/* Value Input */}
      {showValue && (
        <div className="flex-1 min-w-[150px]">
          <StandardInput
            value={String(condition.value || '')}
            onChange={(e) => onUpdate(index, 'value', e.target.value)}
            placeholder="Value"
            disabled={disabled}
          />
        </div>
      )}

      {/* Reorder Buttons */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <StandardButton
          size="xs"
          variant="ghost"
          onClick={() => onMoveUp(index)}
          disabled={isFirst || disabled}
          title="Move Up"
        >
          <ArrowUp size={14} />
        </StandardButton>
        <StandardButton
          size="xs"
          variant="ghost"
          onClick={() => onMoveDown(index)}
          disabled={isLast || disabled}
          title="Move Down"
        >
          <ArrowDown size={14} />
        </StandardButton>
      </div>

      {/* Remove Button */}
      <StandardButton
        size="xs"
        variant="ghost"
        onClick={() => onRemove(index)}
        disabled={disabled}
        title="Remove Condition"
      >
        <Trash2 size={14} className="text-red-500" />
      </StandardButton>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const CriteriaBuilder: React.FC<CriteriaBuilderProps> = ({
  conditions,
  onChange,
  conditionLogic,
  onLogicChange,
  customLogic,
  onCustomLogicChange,
  objectType,
  title,
  description,
  showLogicOptions = true,
  disabled
}) => {
  // Handlers
  const handleAddCondition = () => {
    const newCondition: ProcessConditionInput = {
      field: '',
      operator: 'equals',
      value: ''
    };
    onChange([...conditions, newCondition]);
  };

  const handleUpdateCondition = (index: number, field: keyof ProcessConditionInput, value: any) => {
    const updated = [...conditions];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleRemoveCondition = (index: number) => {
    const updated = conditions.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...conditions];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === conditions.length - 1) return;
    const updated = [...conditions];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  };

  // Generate logic preview
  const getLogicPreview = () => {
    if (conditions.length === 0) return 'No conditions defined';
    if (conditions.length === 1) return 'Condition 1';

    if (conditionLogic === 'CUSTOM' && customLogic) {
      return customLogic;
    }

    const indices = conditions.map((_, i) => i + 1);
    return indices.join(` ${conditionLogic} `);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      {(title || description) && (
        <div>
          {title && (
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {title}
            </h4>
          )}
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      )}

      {/* Logic Selector */}
      {showLogicOptions && conditions.length > 1 && (
        <div className="space-y-3">
          <StandardSelect
            label="Filter Logic"
            value={conditionLogic}
            onChange={(e) => onLogicChange(e.target.value as 'AND' | 'OR' | 'CUSTOM')}
            options={LOGIC_OPTIONS}
            disabled={disabled}
          />

          {conditionLogic === 'CUSTOM' && onCustomLogicChange && (
            <div>
              <StandardInput
                label="Custom Logic Expression"
                value={customLogic || ''}
                onChange={(e) => onCustomLogicChange(e.target.value)}
                placeholder="e.g., (1 AND 2) OR 3"
                disabled={disabled}
                helpText="Use numbers to reference conditions and AND/OR operators"
              />
            </div>
          )}

          {/* Logic Preview */}
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
            <Info size={14} className="text-blue-600 dark:text-blue-400" />
            <span className="text-blue-800 dark:text-blue-200">
              Logic: <code className="font-mono">{getLogicPreview()}</code>
            </span>
          </div>
        </div>
      )}

      {/* Conditions List */}
      <div className="space-y-2">
        {conditions.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            <p className="text-sm">No conditions defined</p>
            <p className="text-xs mt-1">Click "Add Condition" to get started</p>
          </div>
        ) : (
          conditions.map((condition, index) => (
            <ConditionRow
              key={condition.id || index}
              condition={condition}
              index={index}
              objectType={objectType}
              onUpdate={handleUpdateCondition}
              onRemove={handleRemoveCondition}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              isFirst={index === 0}
              isLast={index === conditions.length - 1}
              disabled={disabled}
            />
          ))
        )}
      </div>

      {/* Add Condition Button */}
      <StandardButton
        variant="secondary"
        size="sm"
        onClick={handleAddCondition}
        leftIcon={<Plus size={16} />}
        disabled={disabled}
      >
        Add Condition
      </StandardButton>
    </div>
  );
};

export default CriteriaBuilder;
